import { useEffect, useRef } from "react";
import { PALETTE } from "./tokens";

/* Lightweight Canvas particle field for the CoBrain hero. ~28 amber
   dots drifting on a slow vector with subtle pulse. Pauses if
   prefers-reduced-motion or the tab is hidden. ~3kb runtime. */

type P = { x: number; y: number; vx: number; vy: number; r: number; a: number; ap: number };

export const HeroParticles = () => {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext("2d"); if (!ctx) return;

    let raf = 0;
    let w = 0, h = 0, dpr = 1;
    let particles: P[] = [];

    const resize = () => {
      const parent = canvas.parentElement; if (!parent) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = parent.clientWidth; h = parent.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = w + "px"; canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // re-seed
      const count = Math.max(18, Math.min(34, Math.round((w * h) / 38000)));
      particles = new Array(count).fill(0).map(() => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: 1.1 + Math.random() * 1.6,
        a: 0.18 + Math.random() * 0.30,
        ap: (Math.random() - 0.5) * 0.004,
      }));
    };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy; p.a += p.ap;
        if (p.a < 0.10 || p.a > 0.55) p.ap *= -1;
        if (p.x < -10) p.x = w + 10; else if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10; else if (p.y > h + 10) p.y = -10;
        ctx.beginPath();
        ctx.fillStyle = `rgba(196,130,58,${p.a.toFixed(3)})`;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };

    const onVis = () => {
      if (document.hidden) cancelAnimationFrame(raf);
      else raf = requestAnimationFrame(tick);
    };

    resize();
    raf = requestAnimationFrame(tick);
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <>
      <canvas
        ref={ref}
        aria-hidden
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          pointerEvents: "none", zIndex: 1, mixBlendMode: "multiply",
          opacity: 0.7,
        }}
      />
      {/* Faint amber scanline sweep — every 6s */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
        overflow: "hidden",
      }}>
        <style>{`
          @keyframes cb-hero-scanline {
            0% { transform: translateY(-20%); opacity: 0; }
            12% { opacity: 0.55; }
            55% { opacity: 0.55; }
            70% { transform: translateY(120%); opacity: 0; }
            100% { transform: translateY(120%); opacity: 0; }
          }
          @media (prefers-reduced-motion: reduce) { .cb-hero-scanline { display: none; } }
        `}</style>
        <div className="cb-hero-scanline" style={{
          position: "absolute", left: 0, right: 0, height: 120,
          background: `linear-gradient(180deg, transparent, ${PALETTE.amber}1F 45%, ${PALETTE.amber}33 50%, ${PALETTE.amber}1F 55%, transparent)`,
          filter: "blur(2px)",
          animation: "cb-hero-scanline 6.5s ease-in-out infinite",
          willChange: "transform, opacity",
        }} />
      </div>
    </>
  );
};

// Touch PALETTE so unused-import lint stays quiet across configs.
void PALETTE;